// frontend/src/utils/httmExcelExport.ts
import { format } from 'date-fns';
import { saveAs } from 'file-saver';
import type { HttmConfig, HttmEvent } from '@/types/httmSchedule';
import {
  calculateH2o2Dose,
  calculatePraziproDose,
  AIRSTONE_COUNT,
  formatVolume,
  CHECKLIST_ITEMS,
} from '@/types/httmSchedule';
import { trackEvent } from '@/utils/analytics';

export async function exportHttmToExcel(
  events: HttmEvent[],
  config: HttmConfig
): Promise<void> {
  // Lazy load xlsx to keep initial bundle small
  const XLSX = await import('xlsx-js-style');

  trackEvent('file_download', {
    file_name: `HTTM_Schedule_${config.startDate}.xlsx`,
    file_extension: 'xlsx',
    item_category: 'public_tools',
  });

  const wb = XLSX.utils.book_new();

  const h2o2Dose = calculateH2o2Dose(
    config.bathVolumeLiters,
    config.h2o2Concentration
  );
  const praziDose = calculatePraziproDose(config.qtTankSizeLiters);
  // Build the spreadsheet data
  const data: (string | number)[][] = [
    ['HTTM Quarantine Schedule'],
    ['Generated by Reef Data Hub (reefdatahub.com)'],
    [],
    ['Configuration'],
    ['Start Date:', config.startDate],
    ['Start Time:', config.startTime],
    ['H2O2 Baths:', config.includeH2o2 ? 'Included' : 'Not included'],
    ...(config.includeH2o2
      ? [
          ['Initial H2O2 Bath:', config.initialH2o2Bath ? 'Yes' : 'No'] as (string | number)[],
          [
            'H2O2 Bath Volume:',
            formatVolume(config.bathVolumeLiters, config.units),
          ] as (string | number)[],
          ['H2O2 Concentration:', `${config.h2o2Concentration}%`] as (string | number)[],
        ]
      : []),
    ['PraziPro:', config.includePrazi ? 'Included' : 'Not included'],
    [
      'QT Tank Size:',
      formatVolume(config.qtTankSizeLiters, config.units),
    ],
    [],
    ['Dose Summary'],
    ...(config.includeH2o2
      ? [
          [
            'H2O2 per bath:',
            `${h2o2Dose.toFixed(1)} ml of ${config.h2o2Concentration}% H2O2`,
          ] as (string | number)[],
          [
            'Total H2O2 needed:',
            `${(h2o2Dose * 2).toFixed(1)} ml (2 baths)`,
          ] as (string | number)[],
        ]
      : []),
    ...(config.includePrazi
      ? [
          ['PraziPro per dose:', `${praziDose.toFixed(1)} ml`] as (string | number)[],
          [
            'Total PraziPro needed:',
            `${(praziDose * 2).toFixed(1)} ml (2 doses)`,
          ] as (string | number)[],
        ]
      : []),
    ['Airstones needed:', `${AIRSTONE_COUNT} (disposable - 1 per tank setup)`],
    [],
    // Schedule
    ['Schedule'],
    [
      'Day',
      'Date',
      'By Time',
      'Action',
      'Timing Rule',
      'Location',
      'Notes',
    ],
  ];

  for (const event of events) {
    const actionText = event.skipped
      ? `${event.action} (SKIPPED)`
      : event.action;

    data.push([
      event.day,
      format(event.date, 'EEE, MMM d, yyyy'),
      format(event.date, 'h:mm a'),
      actionText,
      event.timing,
      event.tank,
      event.notes,
    ]);
  }

  // Equipment checklist
  data.push([], ['Equipment Checklist']);
  let currentGroup = '';
  CHECKLIST_ITEMS.forEach((item, index) => {
    if (item.group !== currentGroup) {
      currentGroup = item.group;
      data.push([currentGroup]);
    }
    const checked = (config.checkedItems & (1 << index)) !== 0;
    let label = item.label;
    // Add dynamic quantities
    if (item.id === 'airstones') label += ` - need ${AIRSTONE_COUNT}`;
    if (item.id === 'h2o2')
      label += ` (${config.h2o2Concentration}%) - need ${(h2o2Dose * 2).toFixed(0)} ml total (based on tank size & concentration)`;
    if (item.id === 'prazipro')
      label += ` - need ${(praziDose * 2).toFixed(1)} ml total (based on tank size)`;

    data.push([checked ? '[x]' : '[ ]', label]);
  });

  // Protocol notes
  data.push(
    [],
    ['Protocol Notes:'],
    [
      '- Transfers must happen BEFORE 72 hours to break the Ich lifecycle',
    ],
    [
      '- PraziPro dose: 1 tsp (5ml) per 20 US gallons - treats flukes',
    ],
    [
      `- H2O2 bath: ${h2o2Dose.toFixed(1)} ml of ${config.h2o2Concentration}% H2O2 into ${formatVolume(config.bathVolumeLiters, config.units)} saltwater, 30 min with aeration`,
    ],
    [
      '- Always match salinity and temperature between tanks before transferring',
    ],
    [
      '- Clean and refill the vacated tank after each transfer so it is ready for the next round',
    ],
    [
      '- Discard airstones after each use - do not reuse',
    ],
    [],
    ['Reference Links:'],
    ['HTTM Method in Detail:', 'https://humble.fish/community/threads/hybrid-ttm-to-treat-more-parasites.1765/'],
    ['H2O2 Bath Instructions:', 'https://humble.fish/community/threads/hydrogen-peroxide-bath.1767/'],
    ['H2O2 Fish Compatibility List:', 'https://humble.fish/community/threads/fish-peroxide-compatibility-list.1789/']
  );

  const ws = XLSX.utils.aoa_to_sheet(data);

  // Set column widths for readability
  ws['!cols'] = [
    { wch: 8 }, // Day / checkbox
    { wch: 55 }, // Date / label
    { wch: 10 }, // By Time
    { wch: 35 }, // Action
    { wch: 25 }, // Timing Rule
    { wch: 14 }, // Location
    { wch: 60 }, // Notes
  ];

  XLSX.utils.book_append_sheet(wb, ws, 'HTTM Schedule');

  const wbout = XLSX.write(wb, { bookType: 'xlsx', type: 'array' });
  const blob = new Blob([wbout], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  saveAs(blob, `HTTM_Schedule_${config.startDate}.xlsx`);
}
